// Small, non-secret preferences kept in the browser (localStorage).
// Every access is guarded: storage can be unavailable (private mode, blocked).

import { en, fr, type Strings } from '../i18n/strings';

function get(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function set(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {}
}

export type Language = 'fr' | 'en';

export const LEGAL_VERSION = '1.1';
export const LEGAL_URL = 'https://coinwow.ca/legal';
export const CONTACT_EMAIL = 'legal@coinwow.ca';
export const APP_VERSION = '0.1.0';

export const Prefs = {
  language(): Language | null {
    const v = get('coinwow.language');
    return v === 'fr' || v === 'en' ? v : null;
  },
  setLanguage(l: Language) {
    set('coinwow.language', l);
  },
  deviceLanguage(): Language {
    for (const l of navigator.languages ?? [navigator.language]) {
      if (l?.toLowerCase().startsWith('fr')) return 'fr';
      if (l?.toLowerCase().startsWith('en')) return 'en';
    }
    return 'en';
  },
  currentLanguage(): Language {
    return Prefs.language() ?? Prefs.deviceLanguage();
  },

  acceptedLegalVersion(): string {
    return get('coinwow.legal.version') ?? '';
  },
  acceptedLegalAt(): Date | null {
    const v = get('coinwow.legal.at');
    return v ? new Date(Number(v)) : null;
  },
  acceptLegal() {
    set('coinwow.legal.at', String(Date.now()));
    set('coinwow.legal.version', LEGAL_VERSION);
  },

  hideBalance(): boolean {
    return get('coinwow.hideBalance') === '1';
  },
  setHideBalance(v: boolean) {
    set('coinwow.hideBalance', v ? '1' : null);
  },

  backupDone(): boolean {
    return get('coinwow.backup.done') === '1';
  },
  setBackupDone(v: boolean) {
    set('coinwow.backup.done', v ? '1' : null);
  },

  notifications(): boolean {
    return get('coinwow.notifications') === '1';
  },
  setNotifications(v: boolean) {
    set('coinwow.notifications', v ? '1' : null);
  },

  webWarningsSeen(): boolean {
    return get('coinwow.webwarnings.seen') === '1';
  },
  setWebWarningsSeen() {
    set('coinwow.webwarnings.seen', '1');
  },

  installDismissed(): boolean {
    return get('coinwow.install.dismissed') === '1';
  },
  dismissInstall() {
    set('coinwow.install.dismissed', '1');
  },

  seenTxids(): string[] | null {
    try {
      const v = JSON.parse(get('coinwow.incoming.seen') || 'null');
      return Array.isArray(v) ? v : null;
    } catch {
      return null;
    }
  },
  setSeenTxids(v: string[] | null) {
    set('coinwow.incoming.seen', v === null ? null : JSON.stringify(v.slice(0, 1000)));
  },

  /** Wallet-specific data to clear when the wallet is deleted or replaced. */
  resetWalletData() {
    set('coinwow.backup.done', null);
    set('coinwow.incoming.seen', null);
  },
};

export function stringsFor(l: Language): Strings {
  return l === 'fr' ? fr : en;
}

export function localeFor(l: Language): string {
  return l === 'fr' ? 'fr-CA' : 'en-US';
}
